<template>
  <div class="sign-activity-page">
    <!-- 右上角退出按钮 -->
    <div class="logout-btn" @click="handleLogout">
      <van-icon name="cross" size="20px" color="#fff" />
    </div>

    <!-- 手机号登录弹窗 -->
    <van-dialog
      v-model="showPhoneDialog"
      title="请输入手机号"
      show-cancel-button
      :close-on-click-overlay="false"
      @confirm="handlePhoneConfirm"
      @cancel="handlePhoneCancel"
    >
      <van-field
        v-model="phoneInput"
        type="tel"
        placeholder="请输入手机号"
        maxlength="11"
        :rules="[{ required: true, message: '请输入手机号' }]"
      />
    </van-dialog>

    <!-- 活动页面内容 -->
    <div v-if="activityData" class="activity-content">
      <!-- 背景图片或画布 -->
      <div
        class="activity-canvas"
        :style="canvasStyle"
        @touchstart="handleTouchStart"
        @touchend="handleTouchEnd"
      >
        <!-- 打卡点位 -->
        <div
          v-for="(checkpoint, index) in checkpoints"
          :key="checkpoint.id"
          class="checkpoint-item"
          :class="{ 'checked': isCheckpointChecked(checkpoint.pointIndex) }"
          :style="getCheckpointStyle(checkpoint)"
          @click="handleCheckpointClick(checkpoint)"
        >
          <div class="checkpoint-marker">
            {{ checkpoint.pointIndex }}
          </div>
          <div class="checkpoint-title" v-if="checkpoint.title">
            {{ checkpoint.title }}
          </div>
        </div>
      </div>
    </div>

    <!-- 加载中 -->
    <van-loading v-if="loading" type="spinner" color="#1989fa" vertical>
      加载中...
    </van-loading>

    <!-- 打卡成功提示 -->
    <van-toast v-model="showSuccessToast" type="success" message="打卡成功！" />
  </div>
</template>

<script>
import { getSignActivityDetail, submitSignIn, getUserSignRecords, validateSealPoints } from "@/api/signApi";
import { Toast } from "vant";

export default {
  name: "SignActivity",
  data() {
    return {
      activityId: null,
      ruleCode: null, // 印章规则编码
      phone: "",
      showPhoneDialog: false,
      phoneInput: "",
      loading: false,
      activityData: null,
      checkpoints: [],
      pageConfig: null,
      checkedPoints: [], // 已打卡的点位序号
      showSuccessToast: false,
      touchStartTime: null,
      touchPoints: [],
      // 标准坐标系（用于跨设备兼容，与录入和测试页面保持一致）
      standardWidth: 800,
      standardHeight: 600
    };
  },
  computed: {
    canvasStyle() {
      if (!this.pageConfig) return {};
      const config = this.pageConfig;
      return {
        width: config.width || "100%",
        height: config.height || "100vh",
        backgroundImage: config.backgroundImage
          ? `url(${config.backgroundImage})`
          : "",
        backgroundColor: config.backgroundColor || "#f5f5f5",
        backgroundSize: config.backgroundSize || "cover",
        backgroundPosition: config.backgroundPosition || "center"
      };
    }
  },
  async mounted() {
    // 从URL获取活动ID
    this.activityId = this.$route.query.activityId || this.$route.query.id;
    if (!this.activityId) {
      Toast.fail("活动ID不能为空");
      this.$router.push("/");
      return;
    }

    // 检查本地缓存的手机号
    const cachedPhone = localStorage.getItem("sign_activity_phone");
    if (cachedPhone) {
      this.phone = cachedPhone;
      await this.loadActivityData();
    } else {
      // 显示手机号输入弹窗
      this.showPhoneDialog = true;
    }
  },
  methods: {
    // 处理手机号确认
    async handlePhoneConfirm() {
      const phone = this.phoneInput.trim();
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        Toast.fail("请输入正确的手机号");
        return;
      }
      this.phone = phone;
      localStorage.setItem("sign_activity_phone", phone);
      this.showPhoneDialog = false;
      await this.loadActivityData();
    },
    // 处理手机号取消
    handlePhoneCancel() {
      this.$router.push("/");
    },
    // 加载活动数据
    async loadActivityData() {
      if (!this.activityId) return;
      this.loading = true;
      try {
          const res = await getSignActivityDetail(this.activityId);
        if (res && res.data) {
          this.activityData = res.data.data || res.data;
          this.checkpoints = res.data.checkpoints || [];
          // 保存 ruleCode（从活动详情中获取）
          if (res.data.ruleCode) {
            this.ruleCode = res.data.ruleCode;
          } else if (res.data.data && res.data.data.ruleCode) {
            this.ruleCode = res.data.data.ruleCode;
          }
          // 解析页面配置JSON
          if (res.data.pageConfigJson) {
            try {
              this.pageConfig =
                typeof res.data.pageConfigJson === "string"
                  ? JSON.parse(res.data.pageConfigJson)
                  : res.data.pageConfigJson;
            } catch (e) {
              console.error("解析页面配置失败", e);
            }
          }
          // 加载已打卡点位
          this.loadCheckedPoints();
        }
      } catch (error) {
        console.error("加载活动数据失败", error);
        Toast.fail("加载活动数据失败");
      } finally {
        this.loading = false;
      }
    },
    // 加载已打卡点位（需要调用后端接口获取用户的打卡记录）
    async loadCheckedPoints() {
      if (!this.activityId || !this.phone) return;
      try {
        const res = await getUserSignRecords(this.activityId, this.phone);
        if (res && res.data) {
          this.checkedPoints = res.data.map(record => record.checkpointIndex).filter(Boolean);
        }
      } catch (error) {
        console.error("加载打卡记录失败", error);
      }
    },
    // 获取点位样式
    getCheckpointStyle(checkpoint) {
      const style = {
        position: "absolute",
        left: `${checkpoint.x}px`,
        top: `${checkpoint.y}px`
      };
      if (checkpoint.width) {
        style.width = `${checkpoint.width}px`;
      }
      if (checkpoint.height) {
        style.height = `${checkpoint.height}px`;
      }
      return style;
    },
    // 判断点位是否已打卡
    isCheckpointChecked(pointIndex) {
      return this.checkedPoints.includes(pointIndex);
    },
    // 处理点位点击
    async handleCheckpointClick(checkpoint) {
      if (this.isCheckpointChecked(checkpoint.pointIndex)) {
        Toast("该点位已打卡");
        return;
      }
      // 提交打卡
      await this.submitSignIn(checkpoint.pointIndex);
    },
    // 处理触摸开始（用于电子印章打卡）
    handleTouchStart(event) {
      if (event.touches.length === 4) {
        this.touchStartTime = Date.now();
        this.touchPoints = [];
        
        // 获取 canvas 容器的位置和尺寸
        const canvasElement = event.currentTarget;
        const rect = canvasElement.getBoundingClientRect();
        
        // 收集4个手指的坐标并转换为标准800x600像素坐标
        for (let i = 0; i < event.touches.length; i++) {
          const touch = event.touches[i];
          // 相对于 canvas 容器的坐标
          const x = touch.clientX - rect.left;
          const y = touch.clientY - rect.top;
          
          // 转换为标准800x600像素坐标（保证跨设备兼容）
          const standardX = Math.round((x / rect.width) * this.standardWidth);
          const standardY = Math.round((y / rect.height) * this.standardHeight);
          
          this.touchPoints.push([standardX, standardY]);
        }
        
        console.log('4个手指接触，点位记录（标准坐标）:', this.touchPoints);
      }
    },
    // 处理触摸结束
    async handleTouchEnd(event) {
      if (this.touchPoints.length === 4) {
        const touchDuration = Date.now() - this.touchStartTime;
        // 判断是否为有效的电子印章打卡（4个点，持续时间合理）
        if (touchDuration < 2000 && touchDuration > 100) {
          // 验证点位是否符合规则（调用后端接口验证）
          const checkpointIndex = await this.findCheckpointBySealPoints(
            this.touchPoints
          );
          if (checkpointIndex) {
            await this.submitSignIn(checkpointIndex);
          } else {
            Toast("电子印章验证失败，请重试");
          }
        }
        this.touchPoints = [];
        this.touchStartTime = null;
      }
    },
    // 根据电子印章点位找到对应的打卡点位（需要后端验证）
    async findCheckpointBySealPoints(touchPoints) {
      if (!this.ruleCode) {
        Toast.fail("活动规则编码不存在，无法验证");
        return null;
      }
      try {
        const res = await validateSealPoints(this.ruleCode, touchPoints);
        if (res && res.data) {
          // 兼容新旧返回格式：valid 或 matched
          const isValid = res.data.valid !== undefined ? res.data.valid : res.data.matched;
          if (isValid) {
            return res.data.checkpointIndex;
          }
        }
      } catch (error) {
        console.error("验证电子印章点位失败", error);
      }
      return null;
    },
    // 提交打卡
    async submitSignIn(checkpointIndex) {
      if (!this.phone || !this.activityId) {
        Toast.fail("请先登录");
        return;
      }
      try {
        const res = await submitSignIn({
          activityId: this.activityId,
          checkpointIndex: checkpointIndex,
          phone: this.phone
        });
        if (res && res.code === 200) {
          this.checkedPoints.push(checkpointIndex);
          this.showSuccessToast = true;
          // 重新加载已打卡点位
          setTimeout(() => {
            this.loadCheckedPoints();
          }, 500);
        } else {
          Toast.fail(res.message || "打卡失败");
        }
      } catch (error) {
        console.error("打卡失败", error);
        Toast.fail("打卡失败，请重试");
      }
    },
    // 退出登录
    handleLogout() {
      this.$dialog
        .confirm({
          title: "提示",
          message: "确定要退出吗？退出后将清除登录信息"
        })
        .then(() => {
          localStorage.removeItem("sign_activity_phone");
          this.$router.push("/");
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped lang="less">
.sign-activity-page {
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
}

.logout-btn {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  cursor: pointer;
}

.activity-content {
  width: 100%;
  height: 100vh;
  position: relative;
}

.activity-canvas {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.checkpoint-item {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;

  &:active {
    transform: scale(0.95);
  }

  &.checked {
    opacity: 0.6;
  }
}

.checkpoint-marker {
  width: 40px;
  height: 40px;
  background: #1989fa;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.checkpoint-title {
  margin-top: 5px;
  font-size: 12px;
  color: #333;
  background: rgba(255, 255, 255, 0.8);
  padding: 2px 8px;
  border-radius: 4px;
}

.checkpoint-item.checked .checkpoint-marker {
  background: #07c160;
}
</style>

